/**
 * Authentication Context
 * Manages user authentication state, login/register, and auth persistence.
 * JWT is stored in an httpOnly cookie (set by the server) — never in localStorage.
 * A dedicated authAxios instance (withCredentials: true) is used for all auth calls
 * so the global axios stays clean and third-party SDKs (e.g. Novu) aren't affected.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Dedicated axios instance for our own backend.
// We do NOT set axios.defaults.withCredentials = true because that global flag
// infects third-party SDKs that use axios internally (e.g. Novu), causing them
// to fail when their server returns Access-Control-Allow-Origin: * (wildcard +
// credentials = browser CORS rejection).
const authAxios = axios.create({ withCredentials: true });

/**
 * AuthProvider component
 * Wraps application to provide authentication context to all child components
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state. Strategy:
  // 1. Restore sessionStorage token (iOS Safari fallback — not affected by ITP)
  // 2. Optimistically show cached user so UI doesn't flash
  // 3. Validate against server via /auth/me (uses httpOnly cookie on browsers that support it,
  //    or the Authorization header set from sessionStorage on iOS Safari)
  useEffect(() => {
    const initAuth = async () => {
      // Restore token from sessionStorage and set as Authorization header
      // This is the fallback for iOS Safari which blocks cross-origin httpOnly cookies
      const storedToken = sessionStorage.getItem('authToken');
      if (storedToken) {
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }

      // Optimistic: render from cache immediately so UI doesn't flash.
      // Self-heal: if the cached user is missing both id and _id (stale pre-normalization
      // shape), skip the optimistic set so we don't render with a broken identity.
      const cached = localStorage.getItem('user');
      if (cached) {
        try {
          const cachedUser = JSON.parse(cached);
          // Normalize on load — old login responses had { id } but no _id; getMe had { _id } but no id.
          // Ensure both fields are present so ID comparisons work everywhere.
          if (cachedUser && (cachedUser.id || cachedUser._id)) {
            const normalized = {
              ...cachedUser,
              id:  cachedUser.id  || cachedUser._id,
              _id: cachedUser._id || cachedUser.id,
            };
            setUser(normalized);
            // Persist the normalised shape so subsequent loads don't need to re-heal
            localStorage.setItem('user', JSON.stringify(normalized));
          }
        } catch { /* ignore bad cache */ }
      }

      try {
        const res = await authAxios.get(`${API_BASE_URL}/auth/me`);
        const fresh = res?.data?.user || res?.data?.data;
        if (fresh) {
          // Normalize: login returns { id } but getMe returns a Mongoose doc with { _id }.
          // Always ensure 'id' is set so downstream comparisons like user.id work after refresh.
          const normalized = fresh.id ? fresh : { ...fresh, id: fresh._id };
          setUser(normalized);
          localStorage.setItem('user', JSON.stringify(normalized));
        }
      } catch {
        // No valid session — clear everything
        sessionStorage.removeItem('authToken');
        delete authAxios.defaults.headers.common['Authorization'];
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // 401 interceptor — token expired or invalid: clear local state and redirect to login
  useEffect(() => {
    const responseInterceptor = authAxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          // Don't redirect for auth calls — /auth/me on startup failing just means no session
          const isAuthCall = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/login');

          // Clear all local auth state
          sessionStorage.removeItem('authToken');
          delete authAxios.defaults.headers.common['Authorization'];
          localStorage.removeItem('user');
          setUser(null);

          // For mid-session 401s (real API calls, not startup checks), redirect to login
          // with a clear session-expired message instead of showing a raw error
          if (!isAuthCall && !window.location.pathname.includes('/login')) {
            window.location.href = '/login?expired=true';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => authAxios.interceptors.response.eject(responseInterceptor);
  }, []);

  /**
   * Login user with email and password
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAxios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      const { user: userData, token } = response.data;

      // Store token in sessionStorage for iOS Safari (cleared when browser session ends)
      if (token) {
        sessionStorage.setItem('authToken', token);
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      // Analytics — identify the user in PostHog on login
      try {
        if (window.posthog) {
          window.posthog.identify(userData.id, {
            email: userData.email,
            role:  userData.role,
            name:  `${userData.firstName} ${userData.lastName}`,
          });
        }
      } catch (_) {}

      return userData;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Login failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Programmatically log in with an already-issued token + user object.
   * Used after email verification so the user doesn't have to log in again.
   */
  const loginWithToken = useCallback((userData, token) => {
    if (token) {
      sessionStorage.setItem('authToken', token);
      authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAxios.post(`${API_BASE_URL}/auth/register`, userData);
      const { user: newUser, token } = response.data;

      if (token) {
        sessionStorage.setItem('authToken', token);
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);

      return newUser;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Registration failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user — server clears the httpOnly cookie, we clear local state
   */
  const logout = useCallback(async () => {
    try {
      await authAxios.post(`${API_BASE_URL}/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      sessionStorage.removeItem('authToken');
      delete authAxios.defaults.headers.common['Authorization'];
      localStorage.removeItem('user');
      setUser(null);
      setError(null);

      // Analytics — reset PostHog identity on logout so next session starts fresh
      try { if (window.posthog) window.posthog.reset(); } catch (_) {}
    }
  }, []);

  /**
   * Update user profile information
   */
  const updateProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAxios.put(`${API_BASE_URL}/auth/profile`, profileData);
      const updatedUser = response.data.user;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return updatedUser;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Profile update failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with Google OAuth token
   */
  const googleLogin = useCallback(async (accessToken) => {
    try {
      setLoading(true);
      setError(null);

      // Send only the access token — backend verifies it with Google's servers
      const response = await authAxios.post(`${API_BASE_URL}/auth/google`, {
        accessToken,
      });

      const { user: userData, token } = response.data;

      if (token) {
        sessionStorage.setItem('authToken', token);
        authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return userData;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Google login failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset email
   */
  const resetPassword = useCallback(async (email) => {
    try {
      setLoading(true);
      setError(null);

      await authAxios.post(`${API_BASE_URL}/auth/forgot-password`, { email });

      return true;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Password reset request failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify email with token from email link
   */
  const verifyEmail = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAxios.post(`${API_BASE_URL}/auth/verify-email`, { token });

      return response.data.message;
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Email verification failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Silently re-fetch the current user from the server and update state.
  // Used by ProtectedRoute to detect cross-device email verification.
  const refreshUser = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_BASE_URL}/auth/me`);
      const fresh = res?.data?.user || res?.data?.data;
      if (fresh) {
        setUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
      }
      return fresh;
    } catch { /* silent — cookie may be expired */ }
  }, []);

  // Lightweight patch for updating user fields in context without a full refresh
  const updateUser = useCallback((fields) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...fields };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    loginWithToken,
    register,
    logout,
    updateProfile,
    updateUser,
    googleLogin,
    resetPassword,
    verifyEmail,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use authentication context
 * Must be called within component wrapped by AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
